import { Slider } from '@/components/atoms/slider';
import { NumberInput } from '@/components/molecules/NumberInput';
import { cn } from '@/lib/utils';

interface PercentSliderInputProps {
  value: number;
  onChange: (value: number) => void;
  /** Percentages are typed and dragged in whole points; 12.5% is noise here. */
  max?: number;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
}

/**
 * A percentage, draggable or typeable, bound to one whole number.
 *
 * Dragging shows what a percentage is worth at a glance; typing is the only way
 * to hit an exact figure someone was given. Both write the same clamped
 * integer, so the two controls can never disagree about the stored value.
 */
export function PercentSliderInput({
  value,
  onChange,
  max = 100,
  disabled,
  className,
  'aria-label': ariaLabel,
}: PercentSliderInputProps) {
  const clamp = (next: number) =>
    Number.isFinite(next) ? Math.min(Math.max(Math.round(next), 0), max) : 0;
  const current = clamp(value);

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <Slider
        aria-label={ariaLabel}
        value={[current]}
        min={0}
        max={max}
        step={1}
        disabled={disabled}
        onValueChange={([next]) => onChange(clamp(next))}
        className="flex-1"
      />
      <div className="relative w-20 shrink-0">
        <NumberInput
          inputMode="numeric"
          min={0}
          max={max}
          step={1}
          disabled={disabled}
          aria-label={ariaLabel ? `${ariaLabel} value` : undefined}
          value={current}
          // An emptied box reads as 0% — the slider has to sit somewhere — but
          // the box itself stays empty so the next figure can be typed straight in.
          emptyValue={0}
          onChange={(next) => onChange(clamp(next ?? 0))}
          className="pr-6 text-right"
        />
        <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-muted-foreground">
          %
        </span>
      </div>
    </div>
  );
}
